import _ from 'lodash';
import { PAGE_TYPE } from '../types/pages.type';
import { TRANSFER_TYPE } from '../types/community-transfer.type';
import { TYPES } from '../data/hospital.data';
import { ADMISSION_TYPES } from '../data/admission.data';
import { ALERT_TYPE_TYPE } from '../types/common.type';

// Helper function to map alert type enum values to readable labels
const getAlertTypeLabel = (typeValue) => {
    const alertTypeLabels = {
        [ALERT_TYPE_TYPE.WEEKS]: 'Weekly',
        [ALERT_TYPE_TYPE.BI_WEEKS]: 'Bi-Weekly', 
        [ALERT_TYPE_TYPE.MONTHS]: 'Monthly'
    };
    return alertTypeLabels[typeValue] || _.capitalize(typeValue);
};

/**
 * Generate a clean, consistent description for report subscriptions
 * @param {Object} params - Parameters for description generation
 * @param {Object} params.reportOptions - Report options (type, reportFileType, etc.)
 * @param {string} params.pageType - Page type (PAGE_TYPE)
 * @param {string} params.title - Report title
 * @param {Object} params.headerFilterItems - Header filter items from Redux store
 * @returns {string} Generated description
 */
export const generateReportDescription = ({
    reportOptions,
    pageType,
    title,
    headerFilterItems
}) => {
    const parts = [];

    // Basic report info
    if (reportOptions?.type && reportOptions?.reportFileType) {
        if (reportOptions.reportFileType === 'ALERT') {
            // Handle alert type as enum - it can be an array of alert frequencies
            let alertTypeText = '';
            if (Array.isArray(reportOptions.type)) {
                // Map enum values to readable labels using helper function
                const typeLabels = reportOptions.type.map(type => getAlertTypeLabel(type));
                alertTypeText = typeLabels.join(', ');
            } else {
                // Fallback for single value
                alertTypeText = getAlertTypeLabel(reportOptions.type);
            }
            parts.push(`${alertTypeText} ${_.toUpper(reportOptions.reportFileType)} report`);
        } else {
            parts.push(`${_.capitalize(reportOptions.type)} ${_.toUpper(reportOptions.reportFileType)} report`);
        }
    }

    // Dashboard info
    if (pageType) {
        const pageDisplayName = {
            [PAGE_TYPE.COMMUNITY_TRANSFER]: 'Community Transfer',
            [PAGE_TYPE.HOSPITAL]: 'Hospital',
            [PAGE_TYPE.ADMISSION]: 'Admission',
            [PAGE_TYPE.DECEASED]: 'Deceased',
            [PAGE_TYPE.OVERALL]: 'Overall'
        }[pageType] || _.startCase(pageType);
        parts.push(`from ${pageDisplayName} dashboard`);
    }

    // Card/title info
    if (title && title !== 'Generate Report') {
        parts.push(`focusing on "${title}"`);
    }

    // Add active filters summary
    if (headerFilterItems?.items) {
        const activeFilters = headerFilterItems.items.filter(item =>
            item.isActiveFilter ||
            (item.filterType === "ADT" && item.selectedIds?.length > 0) ||
            (item.filterCount && item.filterCount > 0)
        );

        if (activeFilters.length > 0) {
            parts.push(`with ${activeFilters.length} active filter${activeFilters.length > 1 ? 's' : ''}`);
        }
    }

    // Transfer type info
    if (headerFilterItems?.metadata?.transferType) {
        const transferType = headerFilterItems.metadata.transferType;
        const transferTypeLabels = {
            [TRANSFER_TYPE.AMA]: 'AMA',
            [TRANSFER_TYPE.SNF]: 'SNF',
            [TRANSFER_TYPE.SAFE_DISCHARGE]: 'Safe Discharge',
            [TYPES.PLANNED]: 'Planned Transfers',
            [TYPES.UNPLANNED]: 'Unplanned Transfers',
            [ADMISSION_TYPES.ADMISSION]: 'Admissions',
            [ADMISSION_TYPES.READMISSION]: 'Readmissions'
        };

        if (Array.isArray(transferType) && transferType.length > 0 && !transferType.includes('all')) {
            const typeLabels = transferType.map(type => transferTypeLabels[type] || _.startCase(type));
            if (typeLabels.length > 0) {
                parts.push(`for ${typeLabels.join(', ')}`);
            }
        } else if (typeof transferType === 'string' && transferType !== 'all') {
            const typeLabel = transferTypeLabels[transferType] || _.startCase(transferType);
            parts.push(`for ${typeLabel}`);
        }
    }

    return parts.join(' ') || 'Automated report with current dashboard settings';
};

/**
 * Generate rich report data for saving and display
 * @param {Object} params - Parameters for description generation
 * @param {Object} params.reportOptions - Report options (type, reportFileType, etc.)
 * @param {string} params.pageType - Page type (PAGE_TYPE)
 * @param {string} params.title - Report title
 * @param {Object} params.headerFilterItems - Header filter items from Redux store
 * @param {Array} params.selectedColumns - Selected columns for Excel reports
 * @param {string} params.tableFilterType - Table filter type
 * @param {string} params.cardType - Card type for filtering
 * @returns {Object} Rich report data object
 */
export const generateRichReportData = ({
    reportOptions,
    pageType,
    title,
    headerFilterItems,
    selectedColumns = [],
    tableFilterType,
    cardType
}) => {
    // Generate the basic description
    const description = generateReportDescription({
        reportOptions,
        pageType,
        title,
        headerFilterItems
    });

    // Generate filter chips data
    const filterChips = generateFilterChips(headerFilterItems, cardType);

    // Additional options
    const additionalOptions = [];
    if (reportOptions?.reportFileType === 'EXCEL' && selectedColumns.length > 0) {
        additionalOptions.push({
            type: 'custom_columns',
            label: `Custom Columns (${selectedColumns.length})`,
            value: selectedColumns
        });
    }
    if (tableFilterType && tableFilterType !== "all") {
        additionalOptions.push({
            type: 'table_filter',
            label: 'Table Filter Applied',
            value: tableFilterType
        });
    }
    if (reportOptions?.isSendReportSeparate) {
        additionalOptions.push({
            type: 'separate_delivery',
            label: 'Separate by Facility',
            value: true
        });
    }

    return {
        version: '1.0', // For future compatibility
        description,
        reportConfig: {
            type: reportOptions?.type,
            reportFileType: reportOptions?.reportFileType,
            pageType,
            title,
            interval: reportOptions?.type,
            isSendReportSeparate: reportOptions?.isSendReportSeparate
        },
        filterChips,
        additionalOptions,
        metadata: {
            createdAt: new Date().toISOString(),
            transferType: headerFilterItems?.metadata?.transferType,
            percentageBy: headerFilterItems?.metadata?.percentageBy,
            lockedTotal: headerFilterItems?.metadata?.lockedTotal,
            lockedTotalBy: headerFilterItems?.metadata?.lockedTotalBy
        }
    };
};

/**
 * Generate filter chips for display in report summaries
 * @param {Object} headerFilterItems - Header filter items from Redux store
 * @returns {Array} Array of chip objects with key, label, isLocked, and type
 */
export const generateFilterChips = (headerFilterItems, cardType) => {
    if (!headerFilterItems?.items) return [];

    const chips = [];

    // Add percentage/comparison baseline if exists
    if (headerFilterItems.metadata?.percentageBy && headerFilterItems.metadata?.shouldShowDefault) {
        chips.push({
            key: 'percentage-baseline',
            label: headerFilterItems.metadata.percentageBy,
            isLocked: headerFilterItems.metadata?.lockedTotal && headerFilterItems.metadata?.lockedTotalBy === 'census',
            type: 'baseline'
        });
    }

    // Add all filter items that have selections
    let isMatchedCardType = false;
    headerFilterItems.items.forEach((item, index) => {
        const hasActiveFilter = item.isActiveFilter ||
            (item.filterType === "ADT" && item.selectedIds?.length > 0) ||
            (item.filterCount && item.filterCount > 0);



        if ((hasActiveFilter || item.isTransferTypeMatch) && !isMatchedCardType) {
            let label = item.label || '';
            let displayText = '';
            let selectedNames = [];

            if (item.filterType === "ADT") {
                label = `${item.label}${item.subTitle ? `: ${item.subTitle}` : ''}`.trim();

                // For ADT filters, try multiple data sources
                if (item.selectedItemDetails?.names?.length > 0) {
                    selectedNames = item.selectedItemDetails.names;
                } else if (item.selectedItemDetails?.displayText) {
                    selectedNames = item.selectedItemDetails.displayText.split(', ').filter(Boolean);
                } else if (item.selectedIds?.length > 0 && item.tableData?.length > 0) {
                    // Fallback: manually resolve names from selectedIds and tableData
                    selectedNames = item.selectedIds.map(id => {
                        const foundItem = item.tableData.find(dataItem =>
                            dataItem.id === id || dataItem._id === id || dataItem.value === id
                        );
                        return foundItem?.name || foundItem?.label || foundItem?.title || foundItem?.text || `ID: ${id}`;
                    }).filter(Boolean);
                }
            } else {
                // For regular filters, try multiple data sources
                if (item.selectedItemDetails?.names?.length > 0) {
                    selectedNames = item.selectedItemDetails.names;
                } else if (item.selectedItemDetails?.displayText) {
                    selectedNames = item.selectedItemDetails.displayText.split(', ').filter(Boolean);
                } else if (item.selectedItemNames?.length > 0) {
                    selectedNames = item.selectedItemNames;
                } else if (item.filterData?.length > 0 && item.filterCount > 0) {
                    // Try to get names from filterData if available
                    // This would require access to the actual cardFilter data
                    console.log('Filter item missing selected names:', item.label, item);
                }
            }

            // Create display text from selected names
            if (selectedNames.length > 0) {
                displayText = selectedNames.join(', ');
            }
            
            // Only show if there's a selection or it's a transfer type match
            if (displayText || item.isTransferTypeMatch) {
                let finalLabel = label;

                // Add item names if available, otherwise show as is for transfer type matches
                if (displayText) {
                    // Truncate long lists to keep UI clean
                    if (displayText.length > 50) {
                        if (selectedNames.length > 2) {
                            const firstTwo = selectedNames.slice(0, 2).join(', ');
                            finalLabel = `${label} (${firstTwo} +${selectedNames.length - 2} more)`;
                        } else {
                            finalLabel = `${label} (${displayText})`;
                        }
                    } else {
                        finalLabel = `${label} (${displayText})`;
                    }
                }

                chips.push({
                    key: `filter-${index}`,
                    label: finalLabel,
                    isLocked: item.isLocked || false,
                    type: 'filter',
                    fullText: displayText, // Store full text for tooltip if needed
                    selectedNames: selectedNames // Store the actual names array
                });
            }
        }
        if (item.dataKey === cardType) {
            isMatchedCardType = true;
        }
    });

    return chips;
}; 